export const en = {
    nav: {
      links: [
        { href: "/availability-monitor", label: "Monitor" },
        { href: "/how-it-works", label: "How it works" },
        { href: "/routes", label: "Routes" },
        { href: "/pricing", label: "Pricing" },
        { href: "/blog", label: "Blog" },
      ],
      createNotification: "Create notification",
      dashboard: "My alerts",
      homeLabel: "MachuPing home",
      menuLabel: "Open menu",
      switchLanguage: "Español",
      switchLanguageHref: "/es",
    },
    footer: {
      tagline: "Ticket availability alerts for hard-to-get Machu Picchu routes.",
      disclaimer: "Independent service. Notifications only; no ticket resale.",
      rights: "All rights reserved.",
      columns: {
        Product: [
          { href: "/availability-monitor", label: "Availability monitor" },
          { href: "/ticket-alerts", label: "Ticket alerts" },
          { href: "/restock-alerts", label: "Restock alerts" },
          { href: "/availability", label: "Availability alerts" },
          { href: "/routes", label: "Supported routes" },
          { href: "/how-it-works", label: "How it works" },
          { href: "/pricing", label: "Pricing" },
        ],
        Resources: [
          { href: "/blog", label: "Blog" },
          { href: "/faq", label: "FAQ" },
          { href: "/trust", label: "Trust & safety" },
          { href: "/contact", label: "Contact" },
          { href: "/status", label: "System status" },
        ],
        Legal: [
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ],
      },
    },
    home: {
      metadataTitle: "Machu Picchu ticket alerts and restocks",
      metadataDescription:
        "Get fast email alerts when Machu Picchu ticket availability appears or increases. Pick exact dates, routes, and party size; book on the official provider.",
      badge: "Built for sold-out days",
      title: "Machu Picchu sold out? Get alerted when tickets come back.",
      description:
        "Cancellations and restocks open up constantly. MachuPing watches your exact dates and routes, then emails you the moment seats appear — you book on the official provider.",
      heroCta: "Build your alert",
      secondaryCta: "See supported routes",
      stats: {
        routesMonitored: "Routes monitored",
        notificationSpeed: "Notification speed",
        monitoring: "Monitoring",
        openingsCaught: "Openings caught (7d)",
        alertsSent: "emails sent",
      },
      setupTitle: "Build your alert",
      setupDescription: "Choose routes, dates, and party size. You can review everything before paying.",
      setupCta: "Continue",
      stepsTitle: "How it works",
      steps: [
        { step: "1", title: "Pick your routes", description: "Choose every route and slot you would accept." },
        { step: "2", title: "Set your preferences", description: "Add exact dates and party size, then pay from $9." },
        { step: "3", title: "Get notified & book", description: "We email you the moment it opens — book on the official provider." },
      ],
      fullFlow: "Learn the full flow",
      whyBadge: "Why MachuPing",
      whyTitle: "Built for short availability windows",
      why: [
        { title: "Fast email alerts", description: "We check frequently and email you within minutes when matching inventory appears." },
        { title: "Exactly what you'd book", description: "Choose the routes, dates, and party size you'd accept. We only alert on real matches." },
        { title: "Notifications only", description: "We never hold or resell tickets. You book directly on the official provider." },
        { title: "From $9", description: "$9 for one traveler, +$3 per extra traveler, up to $18. One payment, no subscription." },
      ],
      proofTitle: "See it working right now",
      proofDescription: "Live data from our monitor — recent openings, current stock, and the latest checks.",
      bottomTitle: "Stop refreshing. Start getting notifications.",
      bottomDescription: "Create one focused alert for your routes and dates, and let us watch for you.",
      bottomCta: "Build your alert",
      bottomSecondaryCta: "See pricing",
    },
    alertSetup: {
      eyebrow: "Set up your alert",
      routesLabel: "Which routes work for you?",
      selectAll: "Select all",
      clearAll: "Clear all",
      selectGroup: "Select all",
      backupHint: "Select every route you'd accept — better odds. We keep emailing until your dates pass or you pause.",
      editRoutes: "Edit routes",
      hideRoutes: "Done",
      datesLabel: "When do you want to go?",
      datesHint: "Pick your main date. Add backups if your plans are flexible — we watch every date you choose.",
      travelDate: "Travel date",
      backupDate: "Backup date",
      addBackupDate: "Add a backup date",
      pickDate: "Pick a date",
      partyLabel: "How many tickets?",
      partyHint: "We'll only email you when this many spots open together.",
      setupSummary: "Your alert",
      routes: "Routes",
      chooseRoute: "Choose a route",
      party: "Tickets",
      ticket: "ticket",
      tickets: "tickets",
      addPartySize: "Add party size",
      dates: "Dates",
      chooseDate: "Add a date",
      delivery: "Delivery",
      deliveryDetail: "Email alerts after sign in",
      configured: "You're all set — save to start watching.",
      finishChoices: "Add a date to get started.",
      chooseDateCta: "Choose a date to continue",
      reviewBeforePayment: "No charge until you review and confirm — then from $9, no subscription.",
      askBeforeSignin: "Have a question before signing in?",
      bookingTips: "Tips to book the instant we email you",
      edit: "Edit",
      done: "Done",
      allRoutes: "All routes",
      routesSelected: "{count} routes",
    },
    bookingReadiness: {
      title: "Be ready to book before your alert arrives",
      description: "Openings can vanish in minutes. A little prep now means you can check out the instant we email you.",
      guideCta: "Read the full booking checklist",
      steps: [
        "Create or sign in to your official TuBoleto account (tuboleto.cultura.pe) ahead of time.",
        "Verify your account email so nothing blocks you at login.",
        "Keep each traveler's passport and full name ready, plus a working payment card.",
        "When our alert email lands, open the booking link and check out immediately — inventory can disappear quickly.",
      ],
    },
    restockProof: {
      title: "Monitoring status",
      status: {
        operational: {
          label: "Live",
          headline: "We're watching for tickets right now",
          sub: "Checks are running normally. You'll get an email the moment new seats open up.",
        },
        degraded: {
          label: "Slightly delayed",
          headline: "Checks are running a little behind",
          sub: "We're still watching, but the last check is older than usual — availability below may lag the official site.",
        },
        stale: {
          label: "Catching up",
          headline: "Our checks are catching up",
          sub: "Monitoring is behind right now. Treat the availability below as historical until it catches up.",
        },
        unknown: {
          label: "Getting started",
          headline: "Monitoring is getting set up",
          sub: "No check has finished yet. Please confirm on the official site before relying on the data below.",
        },
      },
      lastCheckedPrefix: "Last checked",
      metrics: {
        available: {
          label: "Monitored slots with stock",
          help: "Seats we can currently see across the dates we monitor.",
        },
        opened7d: {
          label: "Times tickets opened",
          help: "How often new seats appeared in the last 7 days.",
        },
        lastOpened: {
          label: "Last time tickets opened",
          help: "When we last saw new seats become available.",
        },
        lastChecked: {
          label: "Last checked",
          help: "When we last looked at the official site.",
        },
      },
      slotsWithStock: "Monitored slots with stock",
      nearestTitle: "Tickets you can grab now",
      nearestSub: "Open seats we're seeing on upcoming dates.",
      noCached: "No open seats right now — we'll email you the moment they appear.",
      recentTitle: "Recently opened up",
      recentSub: "New seats we spotted recently.",
      opening: "opening",
      openings: "openings",
      last24h: "in the last 24h",
      noIncreases: "No new seats spotted yet.",
      noneYet: "None yet",
      starting: "Starting",
      seat: "seat",
      seats: "seats",
      at: "at",
      scope: "Machu Picchu availability",
    },
    emails: {
      subject: "Machu Picchu availability: {date} {slot}",
      found: "MachuPing found availability.",
      route: "Route",
      date: "Date",
      slot: "Slot",
      seatsObserved: "Seats observed",
      book: "Book on the official provider",
    },
} as const;

type Widen<T> =
  T extends (...args: infer Args) => infer Return ? (...args: Args) => Return :
  T extends readonly (infer Item)[] ? readonly Widen<Item>[] :
  T extends object ? { [Key in keyof T]: Widen<T[Key]> } :
  T extends string ? string :
  T extends number ? number :
  T extends boolean ? boolean :
  T;

export type SiteCopy = Omit<Widen<typeof en>, "footer"> & {
  footer: Omit<Widen<typeof en.footer>, "columns"> & {
    columns: Record<string, readonly Widen<(typeof en.footer.columns.Product)[number]>[]>;
  };
};
